import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import { ThemeOptions } from '@mui/material/styles';
import { TypographyOptions } from '@mui/material/styles/createTypography';
import { DesignTokens } from './theme.design';


declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    iconBoxNeutral: true;
    iconBoxPrimary: true;
  }
}

declare module '@mui/material/ToggleButton' {
  interface ToggleButtonPropsSizeOverrides {
    numeric: true;
  }
}

export function pixelsToREM(x: number) {
  return `${x / 16}rem`;
}

// @todo fontFamily is needed as a global inside typographyConfig so that linting works. We should fix this in the future.
const typographyConfig: TypographyOptions = {
  fontFamily: DesignTokens.bodyFontFamily,
  h1: {
    fontFamily: DesignTokens.headerFontFamily,
    fontWeight: 300,
    fontStyle: 'normal',
    fontSize: '96px',
    lineHeight: '112px',
    letterSpacing: '-1.5px',
  },
  h2: {
    fontFamily: DesignTokens.headerFontFamily,
    fontWeight: 300,
    fontSize: '60px',
    lineHeight: '72px',
    letterSpacing: '-0.5px',
  },
  h3: {
    fontFamily: DesignTokens.headerFontFamily,
    fontWeight: 400,
    fontStyle: 'normal',
    fontSize: '48px',
    lineHeight: '56px',
    letterSpacing: '0px',
  },
  h4: {
    fontFamily: DesignTokens.headerFontFamily,
    fontWeight: 400,
    fontStyle: 'regular',
    color: DesignTokens.color.text.dark,
    fontSize: '34px',
    lineHeight: '36px',
    letterSpacing: '0px',
  },
  h5: {
    fontFamily: DesignTokens.headerFontFamily,
    fontWeight: 400,
    fontStyle: 'regular',
    fontSize: '24px',
    lineHeight: '24px',
    letterSpacing: '0.18px',
  },
  h6: {
    fontFamily: DesignTokens.headerFontFamily,
    fontWeight: 500,
    fontStyle: 'medium',
    fontSize: '20px',
    lineHeight: '24px',
    letterSpacing: '0px',
  },
  subtitle1: {
    fontFamily: DesignTokens.bodyFontFamily,
    fontWeight: 400,
    fontStyle: 'medium',
    fontSize: '16px',
    lineHeight: '24px',
    letterSpacing: '0.15px',
    color: DesignTokens.color.text.darkGray,
  },
  subtitle2: {
    fontFamily: DesignTokens.bodyFontFamily,
    fontWeight: 500,
    fontStyle: 'medium',
    fontSize: '14px',
    lineHeight: '24px',
    letterSpacing: '0.1px',
    color: DesignTokens.color.text.secondary,
  },
  subtitle3: {
    fontFamily: DesignTokens.bodyFontFamily,
    fontWeight: 600,
    fontStyle: 'normal',
    fontSize: pixelsToREM(14),
    lineHeight: pixelsToREM(20),
  },
  body1: {
    fontFamily: DesignTokens.bodyFontFamily,
    fontWeight: 400,
    fontStyle: 'normal',
    fontSize: '16px',
    lineHeight: '24px',
    letterSpacing: '0.5px',
  },
  body2: {
    fontFamily: DesignTokens.bodyFontFamily,
    fontWeight: 400,
    fontStyle: 'regular',
    fontSize: '14px',
    lineHeight: '20px',
    letterSpacing: '0.25px',
  },
  body3: {
    fontFamily: DesignTokens.bodyFontFamily,
    fontWeight: 400,
    fontStyle: 'normal',
    fontSize: pixelsToREM(14),
    lineHeight: pixelsToREM(20),
  },
  body4: {
    fontFamily: DesignTokens.bodyFontFamily,
    fontWeight: 400,
    fontStyle: 'normal',
    color: DesignTokens.color.text.secondary,
    fontSize: pixelsToREM(12),
    lineHeight: pixelsToREM(16),
  },
  body1Bold: {
    fontFamily: DesignTokens.bodyFontFamily,
    fontWeight: 700,
    fontStyle: 'normal',
    fontSize: pixelsToREM(18),
    lineHeight: pixelsToREM(28),
  },
  body2Bold: {
    fontFamily: DesignTokens.bodyFontFamily,
    fontWeight: 700,
    fontStyle: 'normal',
    fontSize: pixelsToREM(16),
    lineHeight: pixelsToREM(24),
  },
  body3Bold: {
    fontFamily: DesignTokens.bodyFontFamily,
    fontWeight: 700,
    fontStyle: 'normal',
    fontSize: pixelsToREM(14),
    lineHeight: pixelsToREM(20),
  },
  body4Bold: {
    fontFamily: DesignTokens.bodyFontFamily,
    fontWeight: 700,
    fontStyle: 'normal',
    fontSize: pixelsToREM(12),
    lineHeight: pixelsToREM(16),
  },
  caption: {
    fontFamily: DesignTokens.bodyFontFamily,
    fontWeight: 500,
    fontStyle: 'normal',
    fontSize: '12px',
    lineHeight: '16px',
    letterSpacing: '0.35px',
  },
  overline: {
    fontFamily: DesignTokens.bodyFontFamily,
    fontWeight: 400,
    fontStyle: 'normal',
    fontSize: pixelsToREM(11),
    lineHeight: pixelsToREM(16),
  },
  buttonLarge: {
    fontFamily: DesignTokens.bodyFontFamily,
    fontWeight: 500,
    fontStyle: 'normal',
    fontSize: pixelsToREM(20),
    lineHeight: pixelsToREM(30),
    textTransform: 'none',
  },
  buttonMedium: {
    fontFamily: DesignTokens.bodyFontFamily,
    fontWeight: 500,
    fontStyle: 'normal',
    fontSize: pixelsToREM(18),
    lineHeight: pixelsToREM(28),
    textTransform: 'none',
  },
  buttonSmall: {
    fontFamily: DesignTokens.bodyFontFamily,
    fontWeight: 500,
    fontStyle: 'normal',
    fontSize: pixelsToREM(16),
    lineHeight: pixelsToREM(24),
    textTransform: 'none',
  },
  buttonXSmall: {
    fontFamily: DesignTokens.bodyFontFamily,
    fontWeight: 500,
    fontStyle: 'medium',
    width: '124px',
    height: '32px',
    fontSize: '14px',
    lineHeight: '20px',
    textTransform: 'none',
  },
  linkLarge: {
    fontFamily: DesignTokens.bodyFontFamily,
    fontWeight: 600,
    fontStyle: 'normal',
    fontSize: pixelsToREM(18),
    lineHeight: pixelsToREM(24),
  },
  linkMedium: {
    fontFamily: DesignTokens.bodyFontFamily,
    fontWeight: 600,
    fontStyle: 'normal',
    fontSize: pixelsToREM(16),
    lineHeight: pixelsToREM(20),
  },
  linkSmall: {
    fontFamily: DesignTokens.bodyFontFamily,
    fontWeight: 600,
    fontStyle: 'normal',
    fontSize: pixelsToREM(14),
    lineHeight: pixelsToREM(16),
  },
  linkXSmall: {
    fontFamily: DesignTokens.bodyFontFamily,
    fontWeight: 600,
    fontStyle: 'normal',
    fontSize: pixelsToREM(12),
    lineHeight: pixelsToREM(16),
  },
  pageHeroTitle: {
    fontFamily: DesignTokens.headerFontFamily,
    fontWeight: 300,
    fontStyle: 'normal',
    fontSize: pixelsToREM(32),
    lineHeight: pixelsToREM(40),
    textAlign: 'center',
    [`@media (min-width: ${DesignTokens.breakpoint.sm}px)`]: {
      fontSize: pixelsToREM(44),
      lineHeight: pixelsToREM(52),
    },
    [`@media (min-width: ${DesignTokens.breakpoint.md}px)`]: {
      fontSize: pixelsToREM(52),
      lineHeight: pixelsToREM(64),
    },
    [`@media (min-width: ${DesignTokens.breakpoint.lg}px)`]: {
      fontSize: pixelsToREM(60),
      lineHeight: pixelsToREM(72),
    },
  },
  pageHeroSubtitle: {
    fontFamily: DesignTokens.headerFontFamily,
    fontWeight: 500,
    fontStyle: 'normal',
    fontSize: pixelsToREM(12),
    lineHeight: pixelsToREM(16),
    textAlign: 'center',
    [`@media (min-width: ${DesignTokens.breakpoint.sm}px)`]: {
      fontSize: pixelsToREM(13),
      lineHeight: pixelsToREM(18),
    },
    [`@media (min-width: ${DesignTokens.breakpoint.md}px)`]: {
      fontSize: pixelsToREM(13.5),
      lineHeight: pixelsToREM(19),
    },
    [`@media (min-width: ${DesignTokens.breakpoint.lg}px)`]: {
      fontSize: pixelsToREM(14),
      lineHeight: pixelsToREM(20),
    },
  },
  footerGridTitle: {
    fontFamily: DesignTokens.headerFontFamily,
    fontWeight: 400,
    fontSize: pixelsToREM(34),
    lineHeight: pixelsToREM(42),
  },
  footerGridHeading: {
    fontFamily: DesignTokens.headerFontFamily,
    fontWeight: 500,
    fontSize: '20px',
    lineHeight: '24px',
    color: DesignTokens.color.secondary.main,
  },
  footerGridLink: {
    fontFamily: DesignTokens.headerFontFamily,
    fontWeight: 400,
    fontSize: '16px',
    lineHeight: '24px',
    color: DesignTokens.color.secondary.main,
  },
  footerGridUpperLinks: {
    fontFamily: DesignTokens.headerFontFamily,
    fontWeight: 400,
    fontSize: '16px',
    lineHeight: '20px',
    color: DesignTokens.color.secondary.lightDark,
  },
  footerTabsLinks: {
    fontFamily: DesignTokens.headerFontFamily,
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '20px',
    color: DesignTokens.color.text.secondary,
  },
  headerNavButton: {
    fontFamily: DesignTokens.bodyFontFamily,
    fontWeight: 500,
    fontSize: pixelsToREM(14),
    lineHeight: pixelsToREM(20),
    textTransform: 'uppercase',
  },
};

export const MuiThemeConfig: ThemeOptions = {
  breakpoints: {
    values: {
      xs: DesignTokens.breakpoint.xs,
      sm: DesignTokens.breakpoint.sm,
      md: DesignTokens.breakpoint.md,
      lg: DesignTokens.breakpoint.lg,
      xl: DesignTokens.breakpoint.xl,
    },
  },
  palette: {
    text: {
      primary: DesignTokens.color.text.primary,
      secondary: DesignTokens.color.text.secondary,
    },
    primary: DesignTokens.color.primary,
    secondary: DesignTokens.color.secondary,
    tertiary: DesignTokens.color.tertiary,
    success: DesignTokens.color.success,
    danger: DesignTokens.color.danger,
    warning: DesignTokens.color.warning,
    mono: DesignTokens.color.mono,
    neutral: DesignTokens.color.neutral,
    info: DesignTokens.color.neutral,
  },
  typography: typographyConfig,
  components: {
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          subtitle3: 'h6',
          body3: 'p',
          body4: 'p',
          body1Bold: 'p',
          body2Bold: 'p',
          body3Bold: 'p',
          body4Bold: 'p',
          pageHeroTitle: 'h1',
          pageHeroSubtitle: 'p',
          footerGridTitle: 'h3',
          footerGridHeading: 'h5',
          footerGridLink: 'p',
          footerGridUpperLinks: 'p',
          footerTabsLinks: 'span',
          headerNavButton: 'span',
        },
      },
    },
    MuiCheckbox: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          color: '#49454F',
          padding: 8,
          transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
          '&:hover': {
            backgroundColor: 'rgba(29, 27, 32, 0.08)',
          },
          '&.Mui-focusVisible': {
            backgroundColor: 'rgba(29, 27, 32, 0.12)',
            boxShadow: '0 0 0 2px #49454F',
          },
          '&:active': {
            backgroundColor: 'rgba(29, 27, 32, 0.12)',
          },
          '&.Mui-checked': {
            color: DesignTokens.color.primary.main,
            '&:hover': {
              backgroundColor: `#6200EE1A`,
            },
            '&.Mui-focusVisible': {
              backgroundColor: `${DesignTokens.color.primary.main}1F`,
              boxShadow: `0 0 0 2px ${DesignTokens.color.primary.main}`,
            },
            '&:active': {
              backgroundColor: `${DesignTokens.color.primary.main}1F`,
            },
          },
          '&.Mui-disabled': {
            opacity: 0.38,
            color: '#1D1B20',
          },
        },
      },
    },

    MuiSwitch: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          width: 64,
          height: 40,
          padding: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'visible',
          '& .MuiSwitch-switchBase': {
            padding: 6,
            top: 0,
            left: 0,
            transitionDuration: '300ms',
            '&:hover': {
              backgroundColor: 'rgba(29, 27, 32, 0.08)',
            },
            '&.Mui-focusVisible': {
              backgroundColor: 'rgba(29, 27, 32, 0.12)',
            },
            '&:active': {
              backgroundColor: 'rgba(29, 27, 32, 0.12)',
            },
            '&.Mui-checked': {
              transform: 'translateX(24px)',
              color: DesignTokens.color.primary.main,
              '& + .MuiSwitch-track': {
                backgroundColor: '#E8DEF8',
                opacity: 1,
                border: 0,
              },
              '& .MuiSwitch-thumb': {
                backgroundColor: DesignTokens.color.primary.main,
              },
              '&:hover': {
                backgroundColor: `#6200EE1A`,
              },
              '&.Mui-focusVisible': {
                backgroundColor: `${DesignTokens.color.primary.main}1F`,
              },
              '&:active': {
                backgroundColor: `${DesignTokens.color.primary.main}1F`,
              },
              '&.Mui-disabled': {
                '& + .MuiSwitch-track': {
                  backgroundColor: '#E8DEF8',
                  opacity: 0.38,
                },
                '& .MuiSwitch-thumb': {
                  backgroundColor: '#FFAB91',
                },
              },
            },
            '&.Mui-disabled': {
              '& + .MuiSwitch-track': {
                backgroundColor: '#E1E1E1',
                opacity: 0.38,
              },
              '& .MuiSwitch-thumb': {
                backgroundColor: '#919191',
              },
            },
          },
          '& .MuiSwitch-thumb': {
            width: 28,
            height: 28,
            boxShadow: '0 2px 4px rgba(0,0,0,0.2), 0 1px 1px rgba(0,0,0,0.1)',
            backgroundColor: '#FFF',
            transition: 'inherit',
          },
          '& .MuiSwitch-track': {
            height: 16,
            width: 52,
            borderRadius: 8,
            backgroundColor: '#EBEBEB',
            opacity: 1,
            transition: 'background-color 500ms cubic-bezier(0.4, 0, 0.2, 1)',
          },
        },
      },
    },
    MuiRadio: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          color: '#49454F',
          padding: 8,
          transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
          '&:hover': {
            backgroundColor: 'rgba(29, 27, 32, 0.08)',
          },
          '&.Mui-focusVisible': {
            backgroundColor: 'rgba(29, 27, 32, 0.12)',
            boxShadow: '0 0 0 2px #49454F',
          },
          '&:active': {
            backgroundColor: 'rgba(29, 27, 32, 0.12)',
          },
          '&.Mui-checked': {
            color: DesignTokens.color.primary.main,
            '&:hover': {
              backgroundColor: `#6200EE1A`,
            },
            '&.Mui-focusVisible': {
              backgroundColor: `${DesignTokens.color.primary.main}1F`,
              boxShadow: `0 0 0 2px ${DesignTokens.color.primary.main}`,
            },
            '&:active': {
              backgroundColor: `${DesignTokens.color.primary.main}1F`,
            },
          },
          '&.Mui-disabled': {
            opacity: 0.38,
            color: '#1D1B20',
          },
        },
      },
    },

    MuiMenuItem: {
      defaultProps: {
        color: 'secondary',
      },
      styleOverrides: {
        root: {
          color: DesignTokens.color.secondary.main,
        },
      },
    },
    MuiSelect: {
      defaultProps: { IconComponent: KeyboardArrowDownOutlinedIcon },
      styleOverrides: {
        icon: {
          color: DesignTokens.color.secondary.main,
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        popupIndicator: {
          display: 'none',
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        fullWidth: true,
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: DesignTokens.color.text.secondary,
          '&.Mui-focused': {
            color: DesignTokens.color.text.secondary, // Gray label even when focused as per "Activated" image
          },
          '&.Mui-error': {
            color: DesignTokens.color.danger.main,
          },
          '&.Mui-disabled': {
            color: DesignTokens.color.text.secondary,
            opacity: 0.38,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: DesignTokens.borderRadius.xs,
          backgroundColor: 'transparent',
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: DesignTokens.color.neutral.gray,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: DesignTokens.color.secondary.light, // Darker gray for focus
            borderWidth: '1px',
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: DesignTokens.color.danger.main,
            borderWidth: '2px',
          },
          '&.Mui-disabled': {
            backgroundColor: 'transparent',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: DesignTokens.color.mono.main,
            },
          },
          [`@media (max-width: ${DesignTokens.breakpoint.sm - 1}px)`]: {
            '& .MuiInputBase-input': {
              height: pixelsToREM(17),
            },
          },
        },
        notchedOutline: {
          borderColor: DesignTokens.color.mono.dark,
          transition:
            'border-color 0.2s ease-in-out, border-width 0.1s ease-in-out',
        },
        input: {
          '&::placeholder': {
            color: DesignTokens.color.text.secondary,
            opacity: 0.8,
            fontSize: '13px',
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginLeft: 0,
          textTransform: 'capitalize',
          '&.Mui-error': {
            color: DesignTokens.color.danger.main,
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
          padding: '24px',
          boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
          backgroundColor: '#FFF',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: 0,
          marginBottom: '16px',
          fontSize: pixelsToREM(24),
          fontWeight: 500,
          color: DesignTokens.color.text.primary,
          fontFamily: DesignTokens.headerFontFamily,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: 0,
          color: DesignTokens.color.text.secondary,
          fontSize: pixelsToREM(16),
          lineHeight: 1.5,
          marginBottom: '24px',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: 0,
          '& .MuiButton-root': {
            fontWeight: 700,
            textTransform: 'uppercase',
            fontSize: pixelsToREM(14),
            padding: '10px 12px',
            color: DesignTokens.color.primary.main,
            '&:hover': {
              backgroundColor: `${DesignTokens.color.primary.main}1A`,
            },
          },
        },
      },
    },
    MuiFab: {
      defaultProps: {
        color: 'primary',
        disableRipple: true,
      },
      variants: [
        {
          props: { color: 'default' },
          style: {
            backgroundColor: '#EBEBEB',
            color: '#1D1B20',
            '&:hover': {
              backgroundColor: '#DADADA',
            },
          },
        },
      ],
      styleOverrides: {
        root: {
          boxShadow:
            '0 3px 5px -1px rgba(0,0,0,0.2), 0 6px 10px 0 rgba(0,0,0,0.14), 0 1px 18px 0 rgba(0,0,0,0.12)',
          '&:active': {
            boxShadow:
              '0 5px 5px -3px rgba(0,0,0,0.2), 0 8px 10px 1px rgba(0,0,0,0.14), 0 3px 14px 2px rgba(0,0,0,0.12)',
          },
          textTransform: 'uppercase',
          fontWeight: 700,
          borderRadius: 100,
          fontFamily: DesignTokens.bodyFontFamily,
        },
        primary: {
          backgroundColor: DesignTokens.color.primary.main,
          color: '#FFF',
          '&:hover': {
            backgroundColor: DesignTokens.color.primary.dark,
          },
        },
        circular: {
          borderRadius: '16px', // MD3 Standard
          width: 56,
          height: 56,
        },
        sizeSmall: {
          width: 40,
          height: 40,
          borderRadius: '12px', // MD3 Mini
        },
        extended: {
          height: 48,
          borderRadius: '24px', // Pill
          padding: '0 20px',
          gap: '8px',
          width: 'auto',
          minWidth: '120px',
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          [`@media (min-width: ${1360}px)`]: {
            maxWidth: '1440px',
          },
        },
      },
    },
    MuiButtonBase: {
      defaultProps: {
        color: 'secondary',
      },
    },
    MuiButton: {
      defaultProps: {
        disableRipple: true,
        disableElevation: true,
      },
      variants: [
        {
          props: { size: 'large' },
          style: {
            ...typographyConfig.buttonMedium,
            textTransform: 'none', // Having textTransform: 'none' inside typographyConfig object gives error
            padding: `${pixelsToREM(8)} ${pixelsToREM(16)}`,
            height: pixelsToREM(48),
            '& .MuiButton-startIcon': {
              '& > *:first-of-type': {
                width: pixelsToREM(20),
                height: pixelsToREM(20),
              },
            },
            '& .MuiButton-endIcon': {
              '& > *:last-of-type': {
                width: pixelsToREM(20),
                height: pixelsToREM(20),
              },
            },
          },
        },
        {
          props: { size: 'medium' },
          style: {
            ...typographyConfig.buttonMedium,
            textTransform: 'none', // Having textTransform: 'none' inside typographyConfig object gives error
            padding: `${pixelsToREM(8)} ${pixelsToREM(12)}`,
            height: pixelsToREM(40),
            '& .MuiButton-startIcon': {
              '& > *:first-of-type': {
                width: pixelsToREM(20),
                height: pixelsToREM(20),
              },
            },
            '& .MuiButton-endIcon': {
              '& > *:last-of-type': {
                width: pixelsToREM(20),
                height: pixelsToREM(20),
              },
            },
          },
        },
        {
          props: { size: 'small' },
          style: {
            ...typographyConfig.buttonSmall,
            textTransform: 'none', // Having textTransform: 'none' inside typographyConfig object gives error
            padding: pixelsToREM(8),
            height: pixelsToREM(32),
            '& .MuiButton-startIcon': {
              '& > *:first-of-type': {
                width: pixelsToREM(16),
                height: pixelsToREM(16),
              },
            },
            '& .MuiButton-endIcon': {
              '& > *:last-of-type': {
                width: pixelsToREM(16),
                height: pixelsToREM(16),
              },
            },
          },
        },
        {
          props: { color: 'tertiary' },
          style: {
            color: DesignTokens.color.neutral.light,
            backgroundColor: DesignTokens.color.tertiary.main,
            '&:hover': {
              color: DesignTokens.color.neutral.light,
              backgroundColor: DesignTokens.color.tertiary.light,
            },
            '&:focus': {
              color: DesignTokens.color.neutral.light,
              backgroundColor: DesignTokens.color.tertiary.light,
            },
            '&:active': {
              color: DesignTokens.color.neutral.light,
              backgroundColor: DesignTokens.color.tertiary.dark,
            },
            '&:disabled': {
              backgroundColor: DesignTokens.color.mono.main,
              color: DesignTokens.color.neutral.gray,
            },
          },
        },
        {
          props: { variant: 'link' },
          style: {
            textDecoration: 'none !important',
            background: 'none !important',
            '&:hover': {
              color: '#FF4D2C',
              textDecoration: 'underline !important',
            },
            '&:focus': {
              color: DesignTokens.color.secondary.light,
            },
            '&:active': {
              color: DesignTokens.color.secondary.dark,
            },
            '&:disabled': {
              color: DesignTokens.color.neutral.gray,
              textDecoration: 'none',
            },
          },
        },
        {
          props: { variant: 'destructive' },
          style: {
            backgroundColor: DesignTokens.color.danger.main,
            color: DesignTokens.color.neutral.light,

            '&:hover': {
              color: DesignTokens.color.neutral.light,
              backgroundColor: DesignTokens.color.danger.light,
            },
            '&:focus': {
              color: DesignTokens.color.neutral.light,
              backgroundColor: DesignTokens.color.danger.light,
            },
            '&:active': {
              color: DesignTokens.color.neutral.light,
              backgroundColor: DesignTokens.color.danger.dark,
            },
            '&:disabled': {
              backgroundColor: DesignTokens.color.mono.main,
              color: DesignTokens.color.neutral.gray,
            },
          },
        },
        {
          props: { variant: 'iconBoxNeutral' },
          style: {
            minWidth: '40px',
            width: '40px',
            height: '40px',
            padding: 0,
            backgroundColor: DesignTokens.color.neutral.light,
            border: '1px solid #0000001F',
            borderRadius: '8px',
            color: DesignTokens.color.text.secondary,
            '&:hover': {
              backgroundColor: DesignTokens.color.mono.main,
            },
          },
        },
        {
          props: { variant: 'iconBoxPrimary' },
          style: {
            minWidth: '40px',
            width: '40px',
            height: '40px',
            padding: 0,
            backgroundColor: DesignTokens.color.primary.accent,
            border: `1px solid ${DesignTokens.color.primary.main}1F`,
            borderRadius: '8px',
            color: DesignTokens.color.primary.main,
            '&:hover': {
              backgroundColor: `${DesignTokens.color.primary.main}14`,
            },
          },
        },
      ],
      styleOverrides: {
        root: {
          borderRadius: pixelsToREM(8),
        },
        containedPrimary: {
          color: DesignTokens.color.neutral.light,
          backgroundColor: DesignTokens.color.primary.main,
          '&:hover': {
            color: DesignTokens.color.neutral.light,
            backgroundColor: DesignTokens.color.primary.light,
          },
          '&:focus': {
            color: DesignTokens.color.neutral.light,
            backgroundColor: DesignTokens.color.primary.light,
          },
          '&:active': {
            color: DesignTokens.color.neutral.light,
            backgroundColor: DesignTokens.color.primary.dark,
          },
          '&:disabled': {
            backgroundColor: DesignTokens.color.mono.main,
            color: DesignTokens.color.neutral.gray,
          },
        },
        containedSecondary: {
          color: DesignTokens.color.neutral.light,
          backgroundColor: DesignTokens.color.secondary.main,
          '&:hover': {
            color: DesignTokens.color.neutral.light,
            backgroundColor: DesignTokens.color.secondary.light,
          },
          '&:focus': {
            color: DesignTokens.color.neutral.light,
            backgroundColor: DesignTokens.color.secondary.light,
          },
          '&:active': {
            color: DesignTokens.color.neutral.light,
            backgroundColor: DesignTokens.color.secondary.dark,
          },
          '&:disabled': {
            backgroundColor: DesignTokens.color.mono.main,
            color: DesignTokens.color.neutral.gray,
          },
        },
        outlined: {
          background: 'none !important',
          borderColor: '#0000001F',
          color: '#FF4D2C',

          '&:hover': {
            backgroundColor: '#6200EE1F !important',
            color: '#FF4D2C',
            borderColor: '#0000001F',
          },
          '&:focus': {
            backgroundColor: DesignTokens.color.mono.main,
            color: DesignTokens.color.secondary.light,
          },
          '&:active': {
            borderColor: DesignTokens.color.secondary.dark,
            backgroundColor: DesignTokens.color.mono.dark,
            color: DesignTokens.color.secondary.dark,
          },
          '&:disabled': {
            color: DesignTokens.color.neutral.gray,
          },
        },
        text: {
          background: 'none !important',
          color: '#FF4D2C',
          '&:hover': {
            backgroundColor: '#6200EE1F !important',
            color: '#FF4D2C',
            borderColor: '#0000001F',
          },
          '&:focus': {
            backgroundColor: DesignTokens.color.mono.main,
            color: DesignTokens.color.secondary.light,
          },
          '&:active': {
            backgroundColor: DesignTokens.color.mono.dark,
            color: DesignTokens.color.secondary.dark,
          },
          // @todo missing transparent background
          '&:disabled': {
            color: DesignTokens.color.neutral.gray,
          },
        },
        startIcon: () => ({
          '& > *:first-of-type': {
            width: pixelsToREM(20),
            height: pixelsToREM(20),
          },
        }),
      },
    },
    MuiToggleButtonGroup: {
      defaultProps: {
        color: 'primary',
      },
      styleOverrides: {
        root: {
          gap: '8px',
          flexWrap: 'wrap',
        },
        grouped: {
          margin: 0,
          border: '1px solid !important',
          borderColor: `${DesignTokens.color.mono.dark} !important`,
          borderRadius: `${DesignTokens.borderRadius.xs} !important`,
          '&.Mui-selected': {
            backgroundColor: `${DesignTokens.color.common.white} !important`,
            color: `${DesignTokens.color.primary.main} !important`,
            borderColor: `${DesignTokens.color.primary.main} !important`,
            zIndex: 1,
            '&:hover': {
              backgroundColor: `${DesignTokens.color.primary.accent} !important`,
            },
          },
          '&:hover': {
            backgroundColor: DesignTokens.color.mono.light,
          },
          '&.Mui-disabled': {
            border: `1px solid ${DesignTokens.color.mono.main} !important`,
          },
        },
      },
    },
    MuiToggleButton: {
      variants: [
        {
          props: { size: 'numeric' },
          style: {
            width: 35,
            height: 34,
            minWidth: 35,
            padding: '6px 6px 6px 8px',
            borderRadius: '4px !important',
            border: `1px solid ${DesignTokens.color.mono.dark} !important`,
            backgroundColor: `${DesignTokens.color.common.white} !important`,
            color: DesignTokens.color.text.primary,
            fontSize: '0.875rem',
            fontWeight: 500,
            justifyContent: 'center',
            alignItems: 'center',
            '&.Mui-selected': {
              borderColor: `${DesignTokens.color.primary.main} !important`,
              color: `${DesignTokens.color.primary.main} !important`,
              backgroundColor: `${DesignTokens.color.common.white} !important`,
              '&:hover': {
                backgroundColor: `${DesignTokens.color.primary.accent} !important`,
              },
            },
            '&:hover': {
              backgroundColor: DesignTokens.color.mono.light,
            },
          },
        },
      ],
      styleOverrides: {
        root: {
          textTransform: 'none',
          padding: '8px 16px',
          minWidth: '44px',
          height: '44px',
          color: DesignTokens.color.text.gray,
          fontWeight: 500,
          fontSize: '0.875rem',
          transition: 'all 0.2s ease',
          backgroundColor: DesignTokens.color.common.white,
          '&:hover': {
            backgroundColor: DesignTokens.color.mono.light,
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: () => ({
          backgroundColor: DesignTokens.color.secondary.main,
          color: DesignTokens.color.common.white,
          fontFamily: DesignTokens.bodyFontFamily,
          fontWeight: 400,
          fontStyle: 'normal',
          fontSize: pixelsToREM(16),
          lineHeight: pixelsToREM(24),
          paddingInline: 16,
          paddingBlock: 12,
          borderRadius: 8,
          display: 'grid',
          gap: 8,
        }),
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          fontFamily: DesignTokens.bodyFontFamily,
          fontWeight: 400,
          fontStyle: 'normal',
          fontSize: pixelsToREM(16),
          lineHeight: pixelsToREM(24),
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          boxShadow: '0 0 10px 0 rgb(0 22 63 / 15%)',
        },
      },
    },
    MuiIconButton: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    MuiSvgIcon: {
      variants: [
        {
          props: { fontSize: 'small' },
          style: {
            fontSize: pixelsToREM(20),
          },
        },
        {
          props: { fontSize: 'medium' },
          style: {
            fontSize: pixelsToREM(20),
          },
        },
        {
          props: { fontSize: 'large' },
          style: {
            fontSize: pixelsToREM(28),
          },
        },
      ],
    },
    MuiPaginationItem: {
      styleOverrides: {
        page: {
          '&.Mui-selected': {
            backgroundColor: DesignTokens.color.secondary.dark,
            color: DesignTokens.color.common.white,
            '&:hover': {
              backgroundColor: DesignTokens.color.secondary.dark,
              color: DesignTokens.color.common.white,
              opacity: 0.9,
            },
          },
        },
      },
    },
    MuiChip: {
      defaultProps: {
        variant: 'outlined',
      },
      styleOverrides: {
        root: {
          borderRadius: pixelsToREM(8),
          height: pixelsToREM(32),
          fontWeight: 500,
          fontSize: pixelsToREM(14),
          lineHeight: pixelsToREM(20),
          border: '1px solid #79747E',
          backgroundColor: 'transparent',
          transition: 'all 0.2s ease-in-out',
          '& .MuiChip-label': {
            paddingLeft: pixelsToREM(12),
            paddingRight: pixelsToREM(12),
          },
          '& .MuiChip-icon': {
            marginLeft: pixelsToREM(8),
            marginRight: pixelsToREM(-4),
            fontSize: pixelsToREM(18),
          },
          '& .MuiChip-deleteIcon': {
            marginRight: pixelsToREM(8),
            marginLeft: pixelsToREM(-4),
            fontSize: pixelsToREM(18),
            color: '#49454F',
            '&:hover': {
              color: '#1D1B20',
            },
          },
        },
        outlined: {
          borderColor: '#79747E',
          color: '#49454F',
          '&:hover': {
            backgroundColor: 'rgba(29, 27, 32, 0.08) !important',
            borderColor: '#79747E',
          },
          '&.Mui-focusVisible': {
            borderColor: '#1D1B20',
            backgroundColor: 'rgba(29, 27, 32, 0.12)',
          },
          '&:active': {
            backgroundColor: 'rgba(29, 27, 32, 0.12)',
            boxShadow: 'none',
          },
          '&.Mui-disabled': {
            opacity: 0.38,
            borderColor: '#1D1B20',
          },
          '&.MuiChip-clickable:hover': {
            backgroundColor: 'rgba(29, 27, 32, 0.08)',
          },
        },
        colorPrimary: {
          '&.Mui-selected': {
            backgroundColor: '#E8DEF8',
            color: '#1D192B',
            borderColor: 'transparent',
            '&:hover': {
              backgroundColor: '#DED3F0',
            },
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paperAnchorBottom: {
          borderTopLeftRadius: pixelsToREM(16),
          borderTopRightRadius: pixelsToREM(16),
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: pixelsToREM(8),
        },
        standardSuccess: {
          backgroundColor: DesignTokens.color.success.accent,
          color: DesignTokens.color.text.primary,
        },
        standardError: {
          backgroundColor: DesignTokens.color.danger.accent,
          color: DesignTokens.color.text.primary,
        },
        standardWarning: {
          backgroundColor: DesignTokens.color.warning.accent,
          color: DesignTokens.color.text.primary,
        },
        standardInfo: {
          backgroundColor: DesignTokens.color.tertiary.accent,
          color: DesignTokens.color.text.primary,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: pixelsToREM(16),
          boxShadow: `0 1px 3px rgba(0, 0, 0, 0.1)`,

          overflow: 'hidden',
          '&:hover': {
            boxShadow: `0 8px 16px rgba(0, 0, 0, 0.15)`,
          },
        },
      },
    },
  },
};
